import React, { FunctionComponent, useEffect } from 'react';
import { connect } from 'react-redux';
import { useSwipeable } from 'react-swipeable';
import { IAppState } from '../../../../common/models/store/IAppState';
import { ISurveyUIPage, SurveyUIPageType } from './utils';
import Question from '../../components/Question';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import { IReferenceSurvey } from '../../../../common/models/reference/IReferenceSurvey';
import {
  confirmReferenceRoutine,
  answerQuestionRoutine,
  fetchQuestionsRoutine,
  setPreviewAnswerRoutine
} from '../../routines';
import SectionBreak from '../../components/SectionBreak';
import { IQuestionForReference } from '../../../../common/models/question/IQuestionForReference';
import { IFetchRefQuestions } from '../../../../common/models/question/IFetchRefQuestions';
import { ISaveRefQuestion } from '../../../../common/models/question/ISaveRefQuestion';
import LoaderWrapper from '../../../../components/LoaderWrapper';
import styles from './styles.module.scss';
import { QuestionSection } from '../../../../common/enums/QuestionSection';
import { ReferenceType } from '../../../../common/enums/ReferenceType';

interface IQuestionsContainerProps {
  reference: IReferenceSurvey;
  questions: IQuestionForReference[];
  isPreview: boolean;
  currentPageIndex: number;
  candidateFirstName: string;
  pages: ISurveyUIPage[];
  isManualInput: boolean;
  fetchQuestions: IBindingCallback1<{ data: IFetchRefQuestions; isManualInput: boolean }>;
  answerQuestion: IBindingCallback1<{ question: ISaveRefQuestion, finish: boolean, isManualInput: boolean }>;
  setPreviewAnswer: IBindingCallback1<Partial<ISaveRefQuestion>>;
  onNextPage: IBindingAction;
  goBack: IBindingAction;
}

const QuestionsContainer: FunctionComponent<IQuestionsContainerProps> = ({
  pages,
  reference,
  questions,
  isPreview,
  isManualInput,
  currentPageIndex,
  candidateFirstName,
  setPreviewAnswer,
  answerQuestion,
  fetchQuestions,
  onNextPage,
  goBack
}) => {
  useEffect(() => {
    if (!questions?.length) {
      const { id, type, surveyId } = reference;
      fetchQuestions({ data: { surveyId, referenceId: id, referenceType: type as ReferenceType }, isManualInput });
    }
  }, []);

  const onAnswerQuestion = ({
    id,
    questionId,
    answer,
    option,
    extraReferenceQuestion: { id: extQuestionId, answer: exAnswer }
  }: IQuestionForReference) => {
    if (!isPreview) {
      answerQuestion({
        isManualInput,
        finish: currentPageIndex === pages.length - 1,
        question: {
          id,
          questionId,
          answer,
          option,
          referenceId: reference.id,
          extraReferenceQuestion: { id: extQuestionId, answer: exAnswer }
        } });
    } else {
      setPreviewAnswer({
        questionId,
        answer,
        extraReferenceQuestion: { id: extQuestionId, answer: exAnswer }
      });
    }
    onNextPage();
  };

  const renderCurrentPage = () => {
    const page = pages[currentPageIndex];

    switch (page?.type) {
      case SurveyUIPageType.Question:
        return (
          <Question
            isPreview={isPreview}
            question={page.content as IQuestionForReference}
            onAnswer={onAnswerQuestion}
            candidateFirstName={candidateFirstName}
          />
        );
      case SurveyUIPageType.SectionBreak:
        return (
          <SectionBreak
            type={page.content as QuestionSection}
            firstName={candidateFirstName}
            onNextPage={onNextPage}
          />
        );
      default:
        return '';
    }
  };

  const pageContent = pages[currentPageIndex] ? pages[currentPageIndex].content as IQuestionForReference : undefined;

  const swipeHandler = useSwipeable({
    onSwipedLeft: () => {
      if ((pageContent && pageContent.answer) || pages[currentPageIndex]?.type === SurveyUIPageType.SectionBreak) {
        onNextPage();
      }
    },
    onSwipedRight: () => (currentPageIndex > 0 ? goBack() : null),
    trackMouse: false
  });

  return (
    <div {...swipeHandler} className="survey-page">
      <LoaderWrapper loading={!questions?.length}>
        <div className={styles.container}>
          <div className="d-flex flex-grow-1 align-items-center justify-content-center w-100">
            {renderCurrentPage()}
          </div>
        </div>
      </LoaderWrapper>
    </div>
  );
};

const mapStateToProps = ({
  refSurvey: { questions, reference, candidate: { firstName }, isManualInput }
}: IAppState) => ({ questions, reference, candidateFirstName: firstName, isManualInput });

const mapDispachToProps = {
  answerQuestion: answerQuestionRoutine,
  fetchQuestions: fetchQuestionsRoutine,
  confirmReference: confirmReferenceRoutine,
  setPreviewAnswer: setPreviewAnswerRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(QuestionsContainer);
